import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as atlas from 'azure-maps-control';
import 'azure-maps-control/dist/atlas.min.css';
import { SvgIcon } from '@progress/kendo-react-common';
import { useLocalization } from '@progress/kendo-react-intl';
import { currentLocation } from '../assets/current-location'
import { renderToStaticMarkup } from 'react-dom/server';
import * as atlasDrawing from 'azure-maps-drawing-tools';
import 'azure-maps-drawing-tools/dist/atlas-drawing.min.css';
import { ExpansionPanel, ExpansionPanelContent } from '@progress/kendo-react-layout';
import { Reveal } from '@progress/kendo-react-animation';
import { getAzureHeatmapColorExpression, getLikelihoodColor } from '../config/likelihoodColors';

type GeoVertex = { longitude: number; latitude: number };


export const AzureMapComponent = (props: any) => {
  const mapContainer = useRef(null);
  const mapRef = useRef<atlas.Map | null>(null);
  const groupMarkersRef = useRef<atlas.HtmlMarker[]>([]);
  const beaconMarkersRef = useRef<atlas.HtmlMarker[]>([]);
  const alarmMarkersRef = useRef<any>([]);

  const drawingManagerRef = useRef<any>(null);

  const localizationService = useLocalization();
  const languageId: any = localStorage.getItem('languageId');

  const [markerLayer, setMarkerLayer] = useState<atlas.source.DataSource | null>(null);
  const [mapReady, setMapReady] = useState(false);

  const [pathStep, setPathStep] = useState(0);
  const [heatmapStep, setHeatmapStep] = useState(0);

  const heatmapLayerRef = useRef<atlas.layer.HeatMapLayer | null>(null);

  const [radius, setRadius] = useState(30);
  const [opacity, setOpacity] = useState(0.8);
  const [intensity, setIntensity] = useState(1.0);

  const [expanded, setExpanded] = useState(false);

  const isValidLongitude = (value: number) => value >= -180 && value <= 180;
  const isValidLatitude = (value: number) => value >= -90 && value <= 90;
  const fallbackCenter: [number, number] = [12.4964, 41.9028]; //Rome

  type CameraMode = 'idle' | 'auto' | 'reset';

  const cameraRef = useRef<{
    initialized: boolean;
    mode: CameraMode;
    lastKey: string;
    baseCenter: [number, number];
  }>({
    initialized: false,
    mode: 'idle',
    lastKey: '',
    baseCenter: fallbackCenter
  });


  const {
    onlyBound,
    groupingEnabled = true,
    alarmInfo,
    alarmToggle,
    geoStaticToggle,
    map,
    devices,
    beacons,
    deviceMac,
    zones,
    zone,
    subscription,
    configZone,
    valueColor,
    referenceZones = [],
    showReferenceZones = false,
    coordinatesList,
    formattedIntervalDate,
    fromDate,
    toDate,
    countWakeups,
    showColor = false,
    showSlider,
    externalSliderPercent,
    onSliderDateChange,
    showAllPoints,
    selectedDevices,
    isHeatmapReady,
    onPolygonVerticesChange
  } = props;

  const hasExternalSliderControl = Number.isFinite(Number(externalSliderPercent));

  const normalizedFromDate = typeof fromDate === 'string' ? fromDate.trim() : '';
  const normalizedFormattedIntervalDate = typeof formattedIntervalDate === 'string' ? formattedIntervalDate.trim() : '';
  const normalizedToDate = typeof toDate === 'string' ? toDate.trim() : '';

  const sliderFromDate = normalizedFromDate || normalizedFormattedIntervalDate || '';
  const sliderToDate = normalizedToDate || new Date().toLocaleString(languageId || undefined);

  let currentMap: any = map;
  if (currentMap == null) {
    try {
      currentMap = JSON.parse(localStorage.getItem('mapSelected') || '{}');
    } catch {
      currentMap = {};
    }
  }

  const zonesList = subscription ? zone : zones;

  const [fromDateUpdated, setFromDateUpdated] = useState<string>('')
  const [toDateUpdated, setToDateUpdated] = useState<string>('')
  const [toDateHeatmapUpdated, setToDateHeatmapUpdated] = useState<string>(normalizedToDate || sliderToDate)
  const [hasTouchedHeatmapSlider, setHasTouchedHeatmapSlider] = useState(false)
  const [infoDevice, setInfoDevice] = useState<any[][]>([])
  const [cleanedCoordinates, setCleanedCoordinates] = useState<[number, number][]>([])

  const getDefaultPathColor = () => `#${infoDevice?.[0]?.[2] ?? 'ff0000'}`;

  const polygonVerticesCallbackRef = useRef<((vertices: GeoVertex[]) => void) | undefined>(onPolygonVerticesChange);
  useEffect(() => {
    polygonVerticesCallbackRef.current = onPolygonVerticesChange;
  }, [onPolygonVerticesChange]);

  const drawingColorRef = useRef(valueColor);
  useEffect(() => {
    drawingColorRef.current = valueColor;
  }, [valueColor]);

  const normalizeCoordinate = (value: any) => {
    const numericValue = Number(value);
    if (!Number.isFinite(numericValue)) {
      return 0;
    }
    return parseFloat(numericValue.toFixed(5));
  };

  const toLocalDisplayDateTime = (value: string) => {
    const raw = typeof value === 'string' ? value.trim() : '';
    if (!raw) {
      return '';
    }

    const direct = new Date(raw);
    if (!Number.isNaN(direct.getTime())) {
      return direct.toLocaleString(languageId || undefined);
    }

    const ymdMatch = raw.match(/^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2})(?::(\d{2}))?)?$/);
    if (ymdMatch) {
      const [, yyyy, mm, dd, hh = '0', min = '0', ss = '0'] = ymdMatch;
      const utcDate = new Date(Date.UTC(
        Number(yyyy),
        Number(mm) - 1,
        Number(dd),
        Number(hh),
        Number(min),
        Number(ss)
      ));

      return utcDate.toLocaleString(languageId || undefined);
    }

    const dmyMatch = raw.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:,?\s+(\d{1,2}):(\d{2})(?::(\d{2}))?)?$/);
    if (dmyMatch) {
      const [, dd, mm, yyyy, hh = '0', min = '0', ss = '0'] = dmyMatch;
      const utcDate = new Date(Date.UTC(
        Number(yyyy),
        Number(mm) - 1,
        Number(dd),
        Number(hh),
        Number(min),
        Number(ss)
      ));

      return utcDate.toLocaleString(languageId || undefined);
    }

    return raw;
  };

  const toValidCenter = (candidate: any): [number, number] | null => {
    if (!Array.isArray(candidate) || candidate.length < 2) {
      return null;
    }

    const longitude = Number(candidate[0]);
    const latitude = Number(candidate[1]);
    if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
      return null;
    }

    if (!isValidLongitude(longitude) || !isValidLatitude(latitude)) {
      return null;
    }

    return [longitude, latitude];
  };

  const roundDownTo3Decimals = (value: number) => Math.floor(value * 10) / 10;
  const roundUpTo3Decimals = (value: number) => Math.ceil(value * 10) / 10;

  const buildPathSegmentFeatures = (maxIndex: number) => {
    const features: atlas.data.Feature<atlas.data.LineString, any>[] = [];

    for (let i = 1; i <= maxIndex && i < cleanedCoordinates.length; i++) {
      const start = cleanedCoordinates[i - 1];
      const end = cleanedCoordinates[i];

      if (!start || !end) {
        continue;
      }

      const likelihood = Number(infoDevice?.[i]?.[5]);
      const segmentColor = showColor
        ? getLikelihoodColor(Number.isFinite(likelihood) ? likelihood : 0)
        : getDefaultPathColor();

      features.push(
        new atlas.data.Feature(
          new atlas.data.LineString([start, end]),
          { color: segmentColor }
        )
      );
    }

    return features;
  };

  const toGeoVertex = (point: any): GeoVertex | null => {
    const longitude = normalizeCoordinate(point?.longitude ?? point?.lon ?? point?.[0]);
    const latitude = normalizeCoordinate(point?.latitude ?? point?.lat ?? point?.[1]);

    if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
      return null;
    }

    if (!isValidLongitude(longitude) || !isValidLatitude(latitude)) {
      return null;
    }

    return { longitude, latitude };
  };

  const pointsAreEqual = (pointA: GeoVertex, pointB: GeoVertex) =>
    Math.abs(pointA.longitude - pointB.longitude) < 1e-6 && Math.abs(pointA.latitude - pointB.latitude) < 1e-6;

  const emitVerticesFromDrawingManager = useCallback(() => {
    const callback = polygonVerticesCallbackRef.current;
    if (!callback) {
      return;
    }

    const manager = drawingManagerRef.current;
    const source: any = manager?.getSource?.();
    if (!source?.toJson) {
      callback([]);
      return;
    }

    const geoJson = source.toJson();
    const features = Array.isArray(geoJson?.features) ? geoJson.features : [];
    const lastPolygonFeature = [...features]
      .reverse()
      .find((feature: any) => feature?.geometry?.type === 'Polygon');

    if (!lastPolygonFeature) {
      callback([]);
      return;
    }

    const firstRing = lastPolygonFeature.geometry?.coordinates?.[0];
    if (!Array.isArray(firstRing) || firstRing.length < 3) {
      callback([]);
      return;
    }

    const vertices: GeoVertex[] = firstRing
      .filter((coordinate: any) => Array.isArray(coordinate) && coordinate.length >= 2)
      .map(([lon, lat]: number[]) => ({
        longitude: normalizeCoordinate(lon),
        latitude: normalizeCoordinate(lat)
      }));

    if (vertices.length >= 2 && pointsAreEqual(vertices[0], vertices[vertices.length - 1])) {
      vertices.pop();
    }

    callback(vertices);
  }, []);

  const setCameraSafe = useCallback((options: atlas.CameraOptions, mode: CameraMode) => {
    const map = mapRef.current;
    if (!map) return;

    const cam = cameraRef.current;

    // ✅ salva centro base SOLO la prima volta
    if (!cam.initialized && options.center) {
      cam.baseCenter = options.center as [number, number];
      cam.initialized = true;
    }

    cam.mode = mode;

    map.setCamera(options);
  }, []);

  useEffect(() => {
    const firstLine = coordinatesList?.[0];
    const rawCoordList = Array.isArray(firstLine?.coord?.[0]) ? firstLine.coord[0] : firstLine?.coord;

    if (!Array.isArray(rawCoordList)) {
      setInfoDevice([]);
      setCleanedCoordinates([]);
      return;
    }

    const normalizedEntries = rawCoordList
      .map((item: any) => {
        if (Array.isArray(item) && item.length >= 2) {
          const latitude = Number(item[0]);
          const longitude = Number(item[1]);

          if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
            return null;
          }

          return {
            lon: longitude,
            lat: latitude,
            color: item[2],
            date: item[3],
            lastPositionDatetime: item[4],
            likelihood: item[5]
          };
        }

        const latitude = Number(item?.lat ?? item?.latitude);
        const longitude = Number(item?.long ?? item?.longitude ?? item?.lon);

        if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
          return null;
        }

        return {
          lon: longitude,
          lat: latitude,
          color: item?.color,
          date: item?.datetime ?? item?.date,
          lastPositionDatetime: item?.lastPositionDatetime,
          likelihood: item?.likelihood
        };
      })
      .filter((entry: any) => entry !== null);

    setInfoDevice(
      normalizedEntries.map((entry: any) => [
        entry.lon,
        entry.lat,
        entry.color,
        entry.date,
        entry.lastPositionDatetime,
        entry.likelihood
      ])
    );
    setCleanedCoordinates(normalizedEntries.map((entry: any) => [entry.lon, entry.lat] as [number, number]));
  }, [coordinatesList])

  const derivedMapCenter = useMemo<[number, number] | null>(() => {
    const west = Number(currentMap?.botLeft?.longitude);
    const east = Number(currentMap?.botRight?.longitude);
    const south = Number(currentMap?.botLeft?.latitude);
    const north = Number(currentMap?.topLeft?.latitude);

    if (![west, east, south, north].every(Number.isFinite)) {
      return null;
    }

    const longitude = (west + east) / 2;
    const latitude = (south + north) / 2;

    return toValidCenter([longitude, latitude]);
  }, [map]);

  const wakeupsBoundingBox = useMemo<[number, number, number, number] | undefined>(() => {
    const points = Array.isArray(coordinatesList) ? coordinatesList.flatMap((line: any) => {
      const rawPoints = Array.isArray(line?.coord?.[0]) ? line.coord[0] : line?.coord;

      if (!Array.isArray(rawPoints)) {
        return [];
      }

      return rawPoints
        .map((point: any) => {
          if (Array.isArray(point) && point.length >= 2) {
            const latitude = Number(point[0]);
            const longitude = Number(point[1]);

            if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
              return null;
            }

            if (!isValidLongitude(longitude) || !isValidLatitude(latitude)) {
              return null;
            }

            return { longitude, latitude };
          }

          const latitude = Number(point?.lat ?? point?.latitude);
          const longitude = Number(point?.long ?? point?.longitude ?? point?.lon);

          if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
            return null;
          }

          if (!isValidLongitude(longitude) || !isValidLatitude(latitude)) {
            return null;
          }

          return { longitude, latitude };
        })
        .filter((point): point is { longitude: number; latitude: number } => point !== null);
    }) : [];

    const mapPoints = [
      currentMap?.botLeft,
      currentMap?.botRight,
      currentMap?.topLeft,
      currentMap?.topRight
    ]
      .map((corner: any) => {
        const longitude = Number(corner?.longitude);
        const latitude = Number(corner?.latitude);

        if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
          return null;
        }

        if (!isValidLongitude(longitude) || !isValidLatitude(latitude)) {
          return null;
        }

        return { longitude, latitude };
      })
      .filter((point): point is { longitude: number; latitude: number } => point !== null);

    const allPoints = points.length > 0 ? points : mapPoints;

    if (!allPoints.length) {
      return undefined;
    }

    const minLongitude = roundDownTo3Decimals(Math.min(...allPoints.map((point) => point.longitude)));
    const minLatitude = roundDownTo3Decimals(Math.min(...allPoints.map((point) => point.latitude)));
    const maxLongitude = roundUpTo3Decimals(Math.max(...allPoints.map((point) => point.longitude)));
    const maxLatitude = roundUpTo3Decimals(Math.max(...allPoints.map((point) => point.latitude)));

    if (minLongitude >= maxLongitude || minLatitude >= maxLatitude) {
      return undefined;
    }

    return [minLongitude, minLatitude, maxLongitude, maxLatitude];
  }, [coordinatesList, currentMap]);

  const getSelectedZones = () => {
    const zonesToProcess = props.subscription ? [zonesList] : zonesList || [];
    const selectedZone: any[] = [];
    const zonesLatLngList: any[] = [];

    if (zonesToProcess.length > 0) {
      zonesToProcess?.forEach((zone: any) => {
        if (zone?.id !== 0) {
          const zoneColor = typeof zone.color === 'string' && zone.color.trim() ? zone.color : '#ff0000';
          const coordinates = zone.points?.map((point: any) => [point.longitude, point.latitude]) || [];

          // Se ci sono coordinate, aggiungiamo la prima alla fine
          if (coordinates.length > 0) {
            coordinates.push(coordinates[0]);
          }

          const newZone = {
            type: 'Feature',
            properties: { fillColor: zoneColor },
            geometry: {
              type: 'Polygon',
              coordinates: [coordinates],
            },
          };

          selectedZone.push(newZone);
          zonesLatLngList.push(coordinates);
        }
      });
    } else {
      const zoneColor = typeof zonesToProcess.color === 'string' && zonesToProcess.color.trim() ? zonesToProcess.color : '#ff0000';
      const coordinates = zonesToProcess.points?.map((point: any) => [point.longitude, point.latitude]) || [];

      // Se ci sono coordinate, aggiungiamo la prima alla fine
      if (coordinates.length > 0) {
        coordinates.push(coordinates[0]);
      }

      const newZone = {
        type: 'Feature',
        properties: { fillColor: zoneColor },
        geometry: {
          type: 'Polygon',
          coordinates: [coordinates],
        },
      };

      selectedZone.push(newZone);
      zonesLatLngList.push(coordinates);
    }

    return { selectedZone, zonesLatLngList };
  };

  useEffect(() => {
    const map = mapRef?.current;
    if (!map) return;

    setMapReady(false);

    const onMapReady = () => {
      setMapReady(true);
    };

    map.events.add('ready', onMapReady);

    return () => {
      map?.events.remove('ready', onMapReady);
    };
  }, [mapRef?.current]);

  useEffect(() => {
    if (!mapContainer.current) return;

    setMapReady(false);

    const map = new atlas.Map(mapContainer.current, {
      style: 'satellite',
      center: derivedMapCenter ?? fallbackCenter,
      zoom: 15.75,
      maxZoom: 20,
      maxBounds: wakeupsBoundingBox,
      language: 'it-IT',
      authOptions: {
        authType: atlas.AuthenticationType.subscriptionKey,
        subscriptionKey: process.env.REACT_APP_AZURE_MAPS_KEY,
      },
      attributionControl: false,
    });

    mapRef.current = map;
    map.events.add('ready', () => {

      // Salva lo stile quando l'utente lo cambia
      map.events.add('stylechanged', () => {
        const currentStyle: any = map.getStyle().style;
        localStorage.setItem('mapStyle', currentStyle); // Salva lo stile in localStorage
      });

      map.controls.add([new atlas.control.ZoomControl(), new atlas.control.FullscreenControl()], {
        position: atlas.ControlPosition.TopRight
      });

      map.controls.add(new atlas.control.StyleControl({
        mapStyles: ['satellite', 'road'],
        layout: 'icons'
      }), {
        position: atlas.ControlPosition.TopRight
      });

      map.controls.add(new atlas.control.ScaleControl({ unit: 'metric' }), {
        position: atlas.ControlPosition.TopRight
      });

      const source = new atlas.source.DataSource();
      map.sources.add(source);
      setMarkerLayer(source);
      setMapReady(true);

    });

    return () => map?.dispose();
  }, [currentMap]);

  //manage zones
  useEffect(() => {
    const map = mapRef?.current;
    if (!map || !mapContainer.current) {
      return;
    }

    const zoneLayerId = 'zone-polygon-layer';
    const zoneSourceId = 'zone-source';

    const removeZoneArtifacts = () => {
      const existingLayer = map.layers.getLayerById(zoneLayerId);
      if (existingLayer) {
        map.layers.remove(existingLayer);
      }

      const existingSource = map.sources.getById(zoneSourceId);
      if (existingSource) {
        map.sources.remove(existingSource);
      }
    };

    // In modalità configurazione gestiamo i poligoni tramite DrawingManager, quindi
    // evitiamo di renderizzare anche il layer statico per prevenire "ombre" residue.
    if (configZone) {
      removeZoneArtifacts();
      return;
    }

    if (!zonesList) {
      removeZoneArtifacts();
      return;
    }

    const applyZones = () => {
      if (!map.sources || !map.layers) {
        return;
      }

      removeZoneArtifacts();

      const hasZones = Array.isArray(zonesList) ? zonesList.length > 0 : Boolean(zonesList);
      if (!hasZones) {
        return;
      }

      const { selectedZone } = getSelectedZones();

      const dataSource: any = new atlas.source.DataSource(zoneSourceId);
      map.sources.add(dataSource);

      dataSource.add({
        type: 'FeatureCollection',
        features: selectedZone
      });

      const polygonLayer = new atlas.layer.PolygonLayer(dataSource, zoneLayerId, {
        fillColor: ['coalesce', ['get', 'fillColor'], 'transparent'],
        fillOpacity: 0.5,
      });

      map.layers.add(polygonLayer);
    };

    if ((map as any)?.ready) {
      applyZones();
      return;
    }

    const readyHandler = () => {
      applyZones();
    };

    map.events.addOnce('ready', readyHandler);

    return () => {
      map.events.remove('ready', readyHandler);
    };
  }, [configZone, zonesList, mapReady]);

  useEffect(() => {
    if (!configZone || !mapReady) {
      return;
    }

    const map = mapRef?.current;
    if (!map) {
      return;
    }

    const referenceLayerId = 'config-zone-reference-layer';
    const referenceSourceId = 'config-zone-reference-source';

    const removeReferenceLayer = () => {
      const existingLayer = map.layers.getLayerById(referenceLayerId);
      if (existingLayer) {
        map.layers.remove(existingLayer);
      }

      const existingSource = map.sources.getById(referenceSourceId);
      if (existingSource) {
        map.sources.remove(existingSource);
      }
    };

    if (!showReferenceZones) {
      removeReferenceLayer();
      return () => {
        removeReferenceLayer();
      };
    }

    const zonesArray = Array.isArray(referenceZones) ? referenceZones : [];

    const features = zonesArray
      .map((zoneEntry: any) => {
        const rawPoints = zoneEntry?.points ?? zoneEntry?.geoPoints;
        if (!Array.isArray(rawPoints)) {
          return null;
        }

        const coordinates: any = rawPoints
          .map((point: any) => {
            const lonCandidate = point?.longitude ?? point?.lon ?? (Array.isArray(point) ? point[0] : point?.x);
            const latCandidate = point?.latitude ?? point?.lat ?? (Array.isArray(point) ? point[1] : point?.y);
            const longitude = normalizeCoordinate(lonCandidate);
            const latitude = normalizeCoordinate(latCandidate);

            if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
              return null;
            }

            return [longitude, latitude];
          })
          .filter((coords: any) => Array.isArray(coords));

        if (coordinates.length < 3) {
          return null;
        }

        const closedRing = [...coordinates, [...coordinates[0]]];

        return {
          type: 'Feature',
          properties: {
            fillColor: zoneEntry?.color || '#1A8894',
            fillOpacity: 0.25
          },
          geometry: {
            type: 'Polygon',
            coordinates: [closedRing]
          }
        };
      })
      .filter(Boolean);

    if (!features.length) {
      removeReferenceLayer();
      return () => {
        removeReferenceLayer();
      };
    }

    removeReferenceLayer();

    const dataSource: any = new atlas.source.DataSource(referenceSourceId);
    map.sources.add(dataSource);

    dataSource.add({
      type: 'FeatureCollection',
      features: features as any
    });

    const polygonLayer = new atlas.layer.PolygonLayer(dataSource, referenceLayerId, {
      fillColor: ['coalesce', ['get', 'fillColor'], '#1A8894'],
      fillOpacity: ['coalesce', ['get', 'fillOpacity'], 0.25]
    });

    map.layers.add(polygonLayer);

    return () => {
      removeReferenceLayer();
    };
  }, [configZone, mapReady, referenceZones, showReferenceZones]);

  //geoStaticToggle
  useEffect(() => {
    if (!mapReady) return;

    const map = mapRef?.current;
    if (!map) return;

    // Rimuove i marker dei beacon
    beaconMarkersRef.current.forEach(marker => map.markers.remove(marker));
    beaconMarkersRef.current = [];

    // Rimuove le zone (layer + source)
    const zoneLayer = map.layers.getLayerById('zone-polygon-layer');
    if (zoneLayer) {
      map.layers.remove(zoneLayer);
    }

    const zoneSource = map.sources.getById('zone-source');
    if (zoneSource) {
      map.sources.remove(zoneSource);
    }

    // Stato zone selezionate
  }, [geoStaticToggle]);

  //moveend - intercetta il movimento della mappa
  //useEffect(() => {
  //  const map = mapRef?.current;
  //  if (!map) return;

  //  const handleMove = () => {
  //    const camera: any = map.getCamera();
  //    setZoom(normalizeZoom(camera?.zoom));
  //  };

  //  map.events.add('moveend', handleMove);
  //  return () => map.events.remove('moveend', handleMove);
  //}, []);

  //markers
  useEffect(() => {
    if (!mapReady || !devices || !markerLayer) return;

    const map: any = mapRef?.current;
    if (!map) return

    markerLayer.clear();

    // 1. Rimuovi i marker dei gruppi precedentemente aggiunti
    groupMarkersRef.current.forEach(marker => {
      map.markers.remove(marker);
    });
    // 2. Pulisci il riferimento
    groupMarkersRef.current = [];

    // 1. Rimuovi i marker dei beacon precedentemente aggiunti
    beaconMarkersRef.current.forEach(marker => {
      map.markers.remove(marker);
    });
    // 2. Pulisci il riferimento
    beaconMarkersRef.current = [];

    // 1. Rimuovi il marker dell'allarme precedente (se esiste)
    if (alarmMarkersRef.current) {
      map.markers.remove(alarmMarkersRef.current);
    }
    // 2. Pulisci il riferimento dell'allarme
    alarmMarkersRef.current = null;

    const popup = new atlas.Popup({ pixelOffset: [0, -18], closeButton: false });

    const addTooltipEvents = () => {
      document.querySelectorAll('.k-icon').forEach((el) => {
        el.addEventListener('mouseenter', (e: any) => {
          const span = e.currentTarget;
          const name = span?.getAttribute('data-name');
          const mac = span?.getAttribute('data-mac');
          const type = span?.getAttribute('data-type');
          const beaconType = span?.getAttribute('data-beacon-type');
          const datetime = span?.getAttribute('data-datetime');

          let html = '<div style="background:white;padding:5px;border-radius:5px;text-align:center">';
          if (type === 'group') {
            html += `<b>${localizationService.toLanguageString('azure.name', 'Name')}: ${name}</b><br><b>Mac: ${mac}</b>`;
          } else if (type === 'beacon') {
            html += `<b>${localizationService.toLanguageString('azure.name', 'Name')}: ${name}</b><br><b>Type: ${beaconType}</b>`;
          } else if (type === 'alarm') {
            html += `<b>${localizationService.toLanguageString('azure.device_mac', 'Device MAC')}: ${mac}</b><br><b>${localizationService.toLanguageString('azure.dateTime', 'DateTime')}: ${datetime}</b>`;
          }
          html += '</div>';

          const position = markerRef[span.getAttribute('data-key')];
          if (position) {
            popup.setOptions({ content: html, position: position });
            popup.open(map);
          }
        });
        el.addEventListener('mouseleave', () => popup.close());
      });
    };

    const markerRef: { [key: string]: any } = {};

    //groups

    /* CLUSTER GROUPS */
    groupMarkersRef.current.forEach(marker => map.markers.remove(marker));
    groupMarkersRef.current = [];

    // Rimuovi layer/sources precedenti
    map.layers.getLayers().forEach((layer: any) => {
      if (layer.id.startsWith("group")) {
        map.layers.remove(layer);
      }
    });

    const groupSourceId = 'group-source';
    // 1. Rimuovi tutti i layer collegati alla source 'group-source'
    map.layers.getLayers().forEach((layer: any) => {
      const source = layer.getSource?.();
      if (source && source.getId && source.getId() === groupSourceId) {
        map.layers.remove(layer);
      }
    });

    // 2. Ora è sicuro rimuovere la source
    const existingGroupSource = map.sources.getById(groupSourceId);
    if (existingGroupSource) {
      map.sources.remove(existingGroupSource);
    }

    // Crea DataSource con clustering
    const groupSource = new atlas.source.DataSource('group-source', {
      cluster: groupingEnabled,
      clusterRadius: groupingEnabled ? 60 : 0
    });

    // Aggiungi gruppi come FeatureCollection
    (Array.isArray(devices) ? devices : JSON.parse(devices || '[]'))?.forEach((group: any, i: number) => {
      if (onlyBound && !group?.name) {
        groupSource?.clear();
        groupSource?.add([]);
        return;
      }
      if (deviceMac && group.mac !== deviceMac) {
        groupSource?.clear();
        groupSource?.add([]);
        return;
      }

      const groupFeatures: any = {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [group?.position?.longitude ?? 0, group?.position?.latitude ?? 0],
        },
        properties: {
          name: group?.name,
          mac: group?.mac,
          color: group?.color || group?.groupColor || '#187D88',
          type: 'group',
          id: `group-${i}`,
        }
      };
      groupSource?.add(groupFeatures);
    });

    ///***  Cluster counts */
    const getSvgImage = (color?: string) => {
      const svg = renderToStaticMarkup(
        <svg viewBox="0 0 24 24" fill={color} width="36" height="36" stroke="white" strokeWidth="1" xmlns="http://www.w3.org/2000/svg">
          <path d="M12 2C8.13 2 5 5.13 5 9c0 4.5 6 11 7 11s7-6.5 7-11c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5s1.12-2.5 2.5-2.5 2.5 1.12 2.5 2.5-1.12 2.5-2.5 2.5z" />
        </svg>
      );
      return `data:image/svg+xml;base64,${btoa(svg)}`;
    };

    const loadSvgImage = (iconId: string, color: string) => {
      return new Promise<void>((resolve) => {
        const img = new Image();
        img.onload = () => {
          map.imageSprite.add(iconId, img);
          resolve();
        };
        img.src = getSvgImage(color);
      });
    };

    const registeredIcons = new Set<string>();



    if (JSON.parse(localStorage.getItem('devicesChecked') || '[]').length === 0) {
      groupSource?.clear();
      groupSource?.add([]);
      return
    }

    const liveStatusMap = localStorage.getItem('liveStatusMap');
    if (liveStatusMap === 'true') {
      const devicesChecked = JSON.parse(localStorage.getItem('devicesChecked') || '[]');
      const devicesLive = JSON.parse(localStorage.getItem('devicesLive') || '[]');
      const matches = devicesLive.filter((live: any) =>
        devicesChecked.some((checked: any) => {
          return checked.mac === live.deviceMac || checked.deviceMac === live.deviceMac;
        })
      );
      if (matches.length == 0) {
        groupSource?.clear();
        groupSource?.add([]);
        return
      }
    }

    const parsedDevices = Array.isArray(devices) ? devices : JSON.parse(devices || '[]');
    groupSource?.clear();

    // Recupera i colori corretti da groupsCheckboxByMap
    const groupsCheckboxByMap = JSON.parse(localStorage.getItem('groupsCheckboxByMap') || '[]');
    const groupColorMap = new Map();
    groupsCheckboxByMap.forEach((group: any) => {
      groupColorMap.set(group.id, group.color);
    });

    const loadAllImages: Promise<void>[] = [];

    parsedDevices?.forEach((device: any) => {

      if ((onlyBound && !device?.name) || (deviceMac && (device.mac !== deviceMac && device.deviceMac !== deviceMac))) {
        return;
      }

      // Usa il colore da groupsCheckboxByMap se disponibile, altrimenti usa quello del device
      const groupColor = groupColorMap.get(device?.groupId) || device?.color || device?.groupColor || '#187D88';
      const colorHex = groupColor.toUpperCase(); // Consistenza
      const iconId = `marker-icon-${colorHex}`;

      if (!registeredIcons.has(iconId)) {
        registeredIcons.add(iconId);
        loadAllImages.push(loadSvgImage(iconId, colorHex));
      }

      const feature = {
        type: 'Feature',
        geometry: {
          type: 'Point',
          coordinates: [
            device?.position?.longitude ?? 0,
            device?.position?.latitude ?? 0,
          ],
        },
        properties: {
          name: device?.name,
          mac: device?.mac ?? device?.deviceMac,
          color: colorHex,
          icon: iconId,
        },
      };

      groupSource.add(feature);
    });

    Promise.all(loadAllImages).then(() => {
      // Solo quando tutte le immagini sono caricate

      const ensureLayerReady = () => {
        if (map?.ready) {
          if (!map.sources.getById('group-source')) {
            map.sources.add(groupSource);
          }
        } else {
          map.events.addOnce('ready', map, () => {
            if (!map.sources.getById('group-source')) {
              map.sources.add(groupSource);
            }
          });
        }

        const unclusteredLayer = new atlas.layer.SymbolLayer(groupSource, 'group-unclustered-symbol', {
          filter: ['!', ['has', 'point_count']],
          iconOptions: {
            image: ['get', 'icon'],
            allowOverlap: true,
            size: 1,
            anchor: 'center',
            padding: 1
          },
        });

        const clusterCount = new atlas.layer.SymbolLayer(groupSource, 'group-cluster-count', {
          filter: ['has', 'point_count'],
          textOptions: {
            textField: ['get', 'point_count_abbreviated'],
            offset: [0, 0],
            allowOverlap: true,
            ignorePlacement: true,
            haloColor: 'white',
            haloWidth: 2
          }
        });

        const addLayers = () => {
          const existingLayers = map.layers.getLayers();

          if (!existingLayers.find((l: any) => l.getId() === 'group-unclustered-symbol')) {
            map.layers.add(unclusteredLayer);
          }

          if (!existingLayers.find((l: any) => l.getId() === 'group-cluster-count')) {
            map.layers.add(clusterCount);
          }

          // Eventi mouseover/mouseout solo una volta
          setTimeout(() => {
            map.events.add('mouseover', unclusteredLayer, (e: any) => {
              const props = e?.shapes[0]?.getProperties();
              const position = e?.shapes[0]?.getCoordinates?.() ?? e?.shapes[0]?.geometry?.coordinates;

              // Calcola la posizione del marker sullo schermo
              const markerPixel = map.positionsToPixels([position])[0];
              const mapCanvas = map.getCanvas();
              const mapWidth = mapCanvas.width;

              // Determina l'offset ottimale in base alla posizione del marker
              let offsetX = 0;
              let offsetY = -25;

              // Gestione angolo alto a destra
              if (markerPixel[0] > mapWidth - 250 && markerPixel[1] < 80) {
                offsetX = -25;
                offsetY = 40;
              }
              // Gestione bordo destro
              else if (markerPixel[0] > mapWidth - 250) {
                offsetX = -25;
                offsetY = 0;
              }
              // Gestione bordo sinistro alto
              else if (markerPixel[0] < 150 && markerPixel[1] < 80) {
                offsetX = 20;
                offsetY = 30;
              }
              // Gestione bordo sinistro
              else if (markerPixel[0] < 150) {
                offsetX = 20;
                offsetY = 0;
              }
              // Gestione bordo superiore
              else if (markerPixel[1] < 80) {
                offsetY = 25;
              }

              const pixelOffset: [number, number] = [offsetX, offsetY];

              const html = `
                <div style="background:white;padding:5px;border-radius:5px;text-align:center;">
                  <b>${localizationService.toLanguageString('azure.name', 'Name')}: ${props?.name == null ? '' : props?.name}</b><br>
                  <b>MAC: ${props?.mac}</b>
                </div>`;
              popup.setOptions({
                content: html,
                position,
                pixelOffset: pixelOffset,
                fillColor: 'white'
              });
              popup.open(map);
              map.getCanvas().style.cursor = 'pointer';
            });

            map.events.add('mouseout', unclusteredLayer, () => {
              popup.close();
              map.getCanvas().style.cursor = '';
            });
          }, 0);
        };

        if (map?.ready) {
          addLayers();
        } else {
          map.events.addOnce('ready', addLayers);
        }
      };


      if (map?.ready) {
        ensureLayerReady();
      } else {
        map.events.addOnce('ready', ensureLayerReady);
      }
    });

    //beacons
    beacons?.filter((b: any) => b?.checked).forEach((beacon: any, i: number) => {
      const color = beacon?.color || 'gray';
      const marker = new atlas.HtmlMarker({
        position: [beacon?.longitude ?? 0, beacon?.latitude ?? 0],
        htmlContent: `<span class="k-icon" style="cursor: pointer; font-size:32px;" data-name="${beacon?.name}" data-type="beacon" data-beacon-type="${beacon?.type}" data-key="beacon-${i}">
        <svg viewBox="0 0 24 24" fill=${color} width="48" height="48" stroke="white" stroke-width="1">
        <path d="M6 6h12v12H6z" />
        </svg>
        </span>`
      });
      markerRef[`beacon-${i}`] = marker.getOptions().position;
      map.markers.add(marker);

      beaconMarkersRef?.current?.push(marker);
    });

    //alarms
    if (alarmToggle && alarmInfo) {
      const localDate = new Date(alarmInfo?.dateTime); // Parses as UTC
      // Convert to local date-time string
      const dateTime = localDate.toLocaleString(); // Uses system locale
      const marker = new atlas.HtmlMarker({
        position: [alarmInfo?.info?.position?.longitude ?? 0, alarmInfo?.info?.position?.latitude ?? 0],
        htmlContent: `<span class="k-icon" style="cursor: pointer;font-size:32px;" data-mac="${alarmInfo?.deviceMac}" data-type="alarm" data-datetime="${dateTime}" data-key="alarm">
          <svg viewBox="0 0 24 24" fill="#ff0000" width="48" height="48" stroke="white" stroke-width="1">
            <path d="M12 2L1 21h22L12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" />
          </svg>
        </span>`
      });
      markerRef[`alarm`] = marker.getOptions().position;
      map.markers.add(marker);
      // Memorizza il marker dell'allarme
      alarmMarkersRef.current = marker;
    }

    addTooltipEvents();
  }, [devices, beacons, alarmInfo, alarmToggle, onlyBound, deviceMac, markerLayer, mapReady, groupingEnabled]);

  useEffect(() => {
    if (!configZone || !mapReady || !mapRef.current || drawingManagerRef.current) return;

    const map = mapRef.current;

    // Crea la toolbar
    const toolbar = new atlasDrawing.control.DrawingToolbar({
      position: 'top-right',
      style: 'light',
      buttons: ['draw-polygon', 'draw-rectangle', 'edit-geometry', 'erase-geometry']
    });

    // Crea il DrawingManager
    const drawingManager = new atlasDrawing.drawing.DrawingManager(map, { toolbar });
    drawingManagerRef.current = drawingManager;

    const handleDrawingComplete = () => {
      const layers: any = drawingManager.getLayers();
      layers?.lineLayer?.setOptions({
        strokeColor: '#1A8894',
        strokeWidth: 2,
      });
      layers?.polygonLayer?.setOptions({
        fillColor: drawingColorRef.current,
        fillOpacity: 0.7
      });
      emitVerticesFromDrawingManager();
    };

    map.events.add('drawingcomplete', drawingManager, handleDrawingComplete);

    return () => {
      map.events.remove('drawingcomplete', drawingManager, handleDrawingComplete);
      drawingManagerRef.current = null;
    };
  }, [configZone, emitVerticesFromDrawingManager, mapReady]);

  useEffect(() => {
    if (!drawingManagerRef.current) return;

    const layers = drawingManagerRef.current.getLayers();
    if (layers && layers.polygonLayer) {
      layers.polygonLayer.setOptions({
        fillColor: valueColor,
        fillOpacity: 0.7
      });
    }
  }, [valueColor]);

  useEffect(() => {
    if (!configZone || !mapReady) {
      return;
    }

    const manager = drawingManagerRef.current;
    const source: atlas.source.DataSource | undefined = manager?.getSource?.();

    if (!manager || !source) {
      return;
    }

    const zoneCandidate = (() => {
      if (!zonesList) {
        return null;
      }
      if (Array.isArray(zonesList)) {
        // Se l'array è vuoto, ritorna null per pulire tutto
        if (zonesList.length === 0) {
          return null;
        }
        return zonesList.find((zoneEntry: any) => Array.isArray(zoneEntry?.points) && zoneEntry.points.length >= 3) || null;
      }
      if (typeof zonesList === 'object') {
        return zonesList;
      }
      return null;
    })();

    source.clear();

    const rawPoints = zoneCandidate?.points || zoneCandidate?.geoPoints;
    const vertices: GeoVertex[] = Array.isArray(rawPoints)
      ? rawPoints
        .map((point: any) => toGeoVertex(point))
        .filter((vertex): vertex is GeoVertex => vertex !== null)
      : [];

    if (vertices.length < 3) {
      // Esce dalla modalità edit per rimuovere i vertici visibili
      manager.setOptions({ mode: 'idle' });
      polygonVerticesCallbackRef.current?.([]);
      return;
    }

    const polygonRing = vertices.map(({ longitude, latitude }) => [longitude, latitude]);
    if (!pointsAreEqual(vertices[0], vertices[vertices.length - 1])) {
      polygonRing.push([vertices[0].longitude, vertices[0].latitude]);
    }

    const polygonShape = new atlas.Shape(new atlas.data.Polygon([polygonRing]));

    source.add(polygonShape);
    try {
      manager.edit(polygonShape);
    } catch {
      source.clear();
      manager.setOptions({ mode: 'idle' });
      polygonVerticesCallbackRef.current?.([]);
      return;
    }

    const layers = manager.getLayers?.();
    layers?.lineLayer?.setOptions({
      strokeColor: '#1A8894',
      strokeWidth: 2
    });
    layers?.polygonLayer?.setOptions({
      fillColor: drawingColorRef.current,
      fillOpacity: 0.7
    });

    polygonVerticesCallbackRef.current?.(vertices);
  }, [configZone, mapReady, zonesList]);

  const resetCenter = () => {
    const cam = cameraRef.current;

    cam.mode = 'reset';
    cam.lastKey = '';

    setCameraSafe({
      center: derivedMapCenter ?? fallbackCenter,
      zoom: 15.75
    }, 'reset');
  };

  const lineDataSourceRef = useRef<any>(null);
  const heatmapDataSourceRef = useRef<any>(null);
  const markersRef = useRef<atlas.HtmlMarker[]>([]);
  const markerRef = useRef<any>(null);
  const popupRef = useRef<any>(null);
  const previousPathLengthRef = useRef<number>(0);
  const previousHeatmapMaxRef = useRef<number>(-1);
  const spaghettiInitialZoom = 17.5;

  const getPathCenter = (coords: [number, number][]) => {
    if (!coords.length) return null;

    let minLon = Infinity, maxLon = -Infinity;
    let minLat = Infinity, maxLat = -Infinity;

    coords.forEach(([lon, lat]) => {
      minLon = Math.min(minLon, lon);
      maxLon = Math.max(maxLon, lon);
      minLat = Math.min(minLat, lat);
      maxLat = Math.max(maxLat, lat);
    });

    return [
      (minLon + maxLon) / 2,
      (minLat + maxLat) / 2
    ] as [number, number];
  };

  useEffect(() => {

    if (!mapReady) return;

    const map = mapRef.current;
    if (!map) return;

    const cam = cameraRef.current;

    if (cam.mode === 'reset') return;

    if (!cleanedCoordinates?.length) return;

    const center = getPathCenter(cleanedCoordinates);
    if (!center) return;

    const key = `${center[0].toFixed(5)}:${center[1].toFixed(5)}:${cleanedCoordinates.length}:${isHeatmapReady}`;

    if (cameraRef.current.lastKey === key) {
      return;
    }


    if (userInteractingRef.current) return;

    setCameraSafe({
      center: center,
      zoom: Math.max(map.getCamera()?.zoom ?? 0, spaghettiInitialZoom),
      //minZoom: spaghettiLockedMinZoom
    }, 'auto');

  }, [mapReady, cleanedCoordinates, isHeatmapReady, showSlider, showAllPoints, zonesList]);

  // manage path_history
  useEffect(() => {
    if (!mapReady) return;

    const map = mapRef?.current;
    if (!map || !mapContainer.current || !map.sources) return;

    if (isHeatmapReady) {
      const existingLayer = map.layers.getLayerById('line-layer');
      if (existingLayer) {
        map.layers.remove(existingLayer);
      }

      const existingSource = map.sources.getById('line-source');
      if (existingSource) {
        map.sources.remove(existingSource);
      }

      if (markerRef.current) {
        map.markers.remove(markerRef.current);
        markerRef.current = null;
      }

      lineDataSourceRef.current = null;
      return;
    }

    if (!cleanedCoordinates?.length) return;

    const addLineLayer = () => {

      const existingLayer = map.layers.getLayerById('line-layer');
      if (existingLayer) map.layers.remove(existingLayer);

      const existingSource = map.sources.getById('line-source');
      if (existingSource) map.sources.remove(existingSource);

      const dataSource = new atlas.source.DataSource('line-source');
      map.sources.add(dataSource);

      const features = buildPathSegmentFeatures(cleanedCoordinates.length - 1);
      if (features.length > 0) {
        dataSource.add(features as any);
      }

      const lineLayer = new atlas.layer.LineLayer(dataSource, 'line-layer', {
        strokeColor: ['get', 'color'],
        strokeWidth: 3
      });

      map.layers.add(lineLayer);
      lineDataSourceRef.current = dataSource;
    };

    try {
      addLineLayer();
    } catch {
      // in caso di errore, aspetta comunque "ready"
    }

    if (showSlider || hasExternalSliderControl) {
      const lastPathIndex = Math.max(cleanedCoordinates.length - 1, 0);

      if (markerRef.current) {
        map.markers.remove(markerRef.current);
      }
      const marker = createPathHistoryMarker(cleanedCoordinates[lastPathIndex]);
      map.markers.add(marker);
      markerRef.current = marker;

      updateLines(lastPathIndex);
      setPathStep(lastPathIndex);

    }
    else {
      if (markerRef.current) {
        map.markers.remove(markerRef.current);
        markerRef.current = null;
      }
    }

    if (!popupRef.current) {
      popupRef.current = new atlas.Popup({ pixelOffset: [0, -30] });
    }

    initMarkerPopup();

    // clean
    return () => {
      if (map && markerRef.current) {
        map.markers.remove(markerRef.current);
        markerRef.current = null;
      }
    };

  }, [mapReady, cleanedCoordinates, showSlider, hasExternalSliderControl, isHeatmapReady, showColor, infoDevice]);

  const showAllMarkers = () => {
    if (!mapRef?.current || !cleanedCoordinates?.length) return;

    const map = mapRef?.current;

    markersRef.current.forEach(marker => map.markers.remove(marker));
    markersRef.current = [];


    cleanedCoordinates.forEach((coord, index) => {

      const dateStr = infoDevice[index]?.[3] || '';
      const [datePart, timePart] = dateStr.split(" ");
      const [day, month, year] = datePart.split("/");
      const [hours, minutes, seconds] = timePart.split(":");

      const utcDate = new Date(Date.UTC(
        year,
        month - 1,
        day,
        hours,
        minutes,
        seconds
      ));

      const localDate = new Date(utcDate);

      //const marker = new atlas.HtmlMarker({
      //  position: coord,
      //  color: '#198692',
      //  text: '●',
      //  //text: `${index + 1}`, // optional
      //  pixelOffset: [0, 0]
      //});

      const marker = createPathHistoryMarker(coord)

      const el = marker.getElement();
      if (el && popupRef.current) {
        el.onmouseenter = () => {
          popupRef.current.setOptions({
            position: coord,
            content: `<div class='popupMarkerLineChart'>
                        <span class='popupMarkerLineChart-vertical-item'>
                          <span class='popupMarkerLineChart-horizontal-item'>${localizationService.toLanguageString('azure.signals', 'NOT_FOUND')}:</span>
                          <span>${countWakeups}</span>
                        </span>
                        <span class='popupMarkerLineChart-vertical-item'>                        
                            <span>${selectedDevices}</span>
                        </span>                    
                        <span class='popupMarkerLineChart-vertical-item'>                        
                            <span class='popupMarkerLineChart-horizontal-item'>
                                ${localizationService.toLanguageString('configuration.analysis.path_history.accuracy', 'NOT_FOUND')}:
                            </span>
                            <span>${infoDevice[index][5].toFixed(2)}</span>
                        </span>
                        <span class='popupMarkerLineChart-vertical-item'>
                            <span class='popupMarkerLineChart-horizontal-item'>
                                D:
                            </span> 
                            <span>
                            ${localDate.toLocaleString()}
                            </span>
                        </span>
                        
                        <span class='popupMarkerLineChart-vertical-item'>
                            <span class='popupMarkerLineChart-horizontal-item'>
                                LD:
                            </span>
                            <span>${infoDevice[index][4]}</span>
                        </span>
                      </div>`,
          });
          popupRef.current.open(map);
        };
        el.onmouseleave = () => {
          popupRef.current.close();
        };
      }

      map.markers.add(marker);
      markersRef.current.push(marker);
    });
  };

  useEffect(() => {
    if (!mapReady || !cleanedCoordinates?.length || !showAllPoints || isHeatmapReady) return;

    const map = mapRef?.current;
    if (!map) return;

    if (!popupRef.current) {
      popupRef.current = new atlas.Popup({ pixelOffset: [0, -30] });
    }

    showAllMarkers();

    // clean
    return () => {
      if (map && markersRef.current.length) {
        markersRef.current.forEach(m => map.markers.remove(m));
        markersRef.current = [];
      }
    };
  }, [showAllPoints, isHeatmapReady, mapReady, cleanedCoordinates]);


  const alignedData = useMemo(() => {
    if (!infoDevice?.length) return [];
    return infoDevice.map((d: any) => ({
      coord: [d[0], d[1]], // [lon, lat]
      date: d[3],
      lastPositionDatetime: d[4],
      likelihood: d[5]
    }));
  }, [infoDevice]);

  const heatmapTimelineDates = useMemo(() => {
    if (!Array.isArray(coordinatesList) || coordinatesList.length === 0) {
      return [] as string[];
    }

    const firstLine = coordinatesList[0];
    const rawPoints = Array.isArray(firstLine?.coord?.[0]) ? firstLine.coord[0] : firstLine?.coord;

    if (!Array.isArray(rawPoints)) {
      return [] as string[];
    }

    return rawPoints
      .map((point: any) => {
        const rawDate = point?.date ?? point?.datetime ?? point?.lastPositionDatetime ?? point?.[3] ?? point?.[4];
        return typeof rawDate === 'string' ? rawDate : '';
      })
      .map((date: string) => date.trim())
      .filter((date: string) => date.length > 0);
  }, [coordinatesList]);

  const localizedHeatmapTimelineDates = useMemo(() => {
    return heatmapTimelineDates.map((date) => toLocalDisplayDateTime(date));
  }, [heatmapTimelineDates, languageId]);
                                                                                        
  const pathSliderStepIndex = Math.min(pathStep, Math.max(alignedData.length - 1, 0));
  const heatmapSliderStepIndex = Math.min(heatmapStep, Math.max(heatmapTimelineDates.length - 1, 0));
  const displayedFromDate = localizedHeatmapTimelineDates[0] || alignedData[0]?.date || fromDateUpdated || sliderFromDate;
  const displayedToDate = alignedData[pathSliderStepIndex]?.date || toDateUpdated || sliderToDate;
  const displayedToDateHeatmap = toDateHeatmapUpdated || normalizedToDate || displayedToDate;

  const getHeatmapPoints = useCallback((line: any): [number, number][] => {
    const rawPoints = Array.isArray(line?.coord?.[0]) ? line.coord[0] : line?.coord;
    if (!Array.isArray(rawPoints)) {
      return [];
    }

    return rawPoints
      .map((point: any) => {
        // Coordinates from API arrays are [lat, lon, ...]; object-based points may already be named.
        const latitude = Number(point?.lat ?? point?.latitude ?? point?.[0]);
        const longitude = Number(point?.long ?? point?.longitude ?? point?.lon ?? point?.[1]);

        if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
          return null;
        }

        return [longitude, latitude] as [number, number];
      })
      .filter((point: [number, number] | null): point is [number, number] => point !== null);
  }, []);

  const getHeatmapPointsWithLikelihood = useCallback((line: any): Array<{ coord: [number, number], likelihood: number }> => {
    const rawPoints = Array.isArray(line?.coord?.[0]) ? line.coord[0] : line?.coord;
    if (!Array.isArray(rawPoints)) {
      return [];
    }

    return rawPoints
      .map((point: any) => {
        const latitude = Number(point?.lat ?? point?.latitude ?? point?.[0]);
        const longitude = Number(point?.long ?? point?.longitude ?? point?.lon ?? point?.[1]);

        if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
          return null;
        }

        const rawLikelihood = Number(point?.likelihood ?? point?.[5]);
        const likelihood = Number.isFinite(rawLikelihood)
          ? Math.max(0, Math.min(1, rawLikelihood))
          : 0;

        return {
          coord: [longitude, latitude] as [number, number],
          likelihood
        };
      })
      .filter((point: { coord: [number, number], likelihood: number } | null): point is { coord: [number, number], likelihood: number } => point !== null);
  }, []);

  const heatmapMaxSlider = useMemo(() => {
    if (!Array.isArray(coordinatesList) || coordinatesList.length === 0) {
      return 0;
    }

    return Math.max(
      ...coordinatesList.map((line: any) => Math.max(getHeatmapPoints(line).length - 1, 0))
    );
  }, [coordinatesList, getHeatmapPoints]);

  useEffect(() => {
    if (heatmapStep > heatmapMaxSlider) {
      setHeatmapStep(heatmapMaxSlider);
    }
  }, [heatmapMaxSlider, heatmapStep]);

  useEffect(() => {
    if (!isHeatmapReady) {
      setHasTouchedHeatmapSlider(false);
      setToDateHeatmapUpdated(normalizedToDate || sliderToDate || '');
      return;
    }

    if (!localizedHeatmapTimelineDates.length) {
      setHasTouchedHeatmapSlider(false);
      setToDateHeatmapUpdated(normalizedToDate || sliderToDate || '');
      return;
    }

    const lastHeatmapIndex = localizedHeatmapTimelineDates.length - 1;
    const clampedHeatmapIndex = hasTouchedHeatmapSlider
      ? Math.min(heatmapStep, lastHeatmapIndex)
      : lastHeatmapIndex;
    setToDateHeatmapUpdated(localizedHeatmapTimelineDates[clampedHeatmapIndex] || normalizedToDate || sliderToDate || '');
  }, [heatmapStep, localizedHeatmapTimelineDates, isHeatmapReady, normalizedToDate, sliderToDate, hasTouchedHeatmapSlider]);

  useEffect(() => {
    setHasTouchedHeatmapSlider(false);
  }, [heatmapTimelineDates]);

  useEffect(() => {
    if (!alignedData.length) {
      previousPathLengthRef.current = 0;
      return;
    }

    if (alignedData.length !== previousPathLengthRef.current) {
      const lastPathIndex = alignedData.length - 1;
      setPathStep(lastPathIndex);
      setFromDateUpdated(alignedData[0]?.date || sliderFromDate || '');
      setToDateUpdated(alignedData[lastPathIndex]?.date || sliderToDate || '');
      updateLines(lastPathIndex);
      previousPathLengthRef.current = alignedData.length;
    }
  }, [alignedData, sliderFromDate, sliderToDate]);

  useEffect(() => {
    if (!isHeatmapReady) {
      previousHeatmapMaxRef.current = -1;
      return;
    }

    if (heatmapMaxSlider !== previousHeatmapMaxRef.current) {
      setHeatmapStep(heatmapMaxSlider);
      updateHeatmap(heatmapMaxSlider);
      previousHeatmapMaxRef.current = heatmapMaxSlider;
    }
  }, [heatmapMaxSlider, isHeatmapReady]);

  useEffect(() => {
    if (!showSlider) {
      return;
    }

    if (!alignedData.length) {
      setPathStep(0);
      setFromDateUpdated(sliderFromDate || '');
      setToDateUpdated(sliderToDate || '');
      return;
    }

    const clampedStep = Math.min(pathStep, alignedData.length - 1);
    if (clampedStep !== pathStep) {
      setPathStep(clampedStep);
      return;
    }

    setFromDateUpdated(alignedData[0]?.date || sliderFromDate || '');
    setToDateUpdated(alignedData[clampedStep]?.date || sliderToDate || '');
  }, [alignedData, showSlider, sliderFromDate, sliderToDate, pathStep]);

  useEffect(() => {
    if (!hasExternalSliderControl) {
      return;
    }

    if (!alignedData.length) {
      setPathStep(0);
      setFromDateUpdated(sliderFromDate || '');
      setToDateUpdated(sliderToDate || '');
      return;
    }

    const normalizedPercent = Math.max(0, Math.min(100, Number(externalSliderPercent)));
    const targetIndex = Math.max(0, Math.min(alignedData.length - 1, Math.round((normalizedPercent / 100) * (alignedData.length - 1))));

    setPathStep(targetIndex);
    setFromDateUpdated(alignedData[0]?.date || sliderFromDate || '');
    setToDateUpdated(alignedData[targetIndex]?.date || sliderToDate || '');
    if (typeof onSliderDateChange === 'function') {
      onSliderDateChange(alignedData[targetIndex]?.date || sliderToDate || '');
    }
    updateLines(targetIndex);
  }, [externalSliderPercent, hasExternalSliderControl, alignedData, sliderFromDate, sliderToDate, onSliderDateChange]);

  const updateLines = (s: number) => {
    if (!lineDataSourceRef.current) return;
    const map = mapRef.current;
    if (!map) return;

    const dataSource = lineDataSourceRef.current;
    dataSource.clear();

    const maxIndex = Math.min(s, alignedData.length - 1);
    const partialPoints = alignedData.slice(0, maxIndex + 1).map((p: any) => p.coord);
    const partialDate = alignedData.slice(0, maxIndex + 1).map((p: any) => p.date);

    const features = buildPathSegmentFeatures(maxIndex);
    if (features.length > 0) {
      dataSource.add(features as any);
    }

    if (!markerRef.current && partialPoints.length > 0 && (showSlider || hasExternalSliderControl)) {
      const initialPoint = partialPoints[partialPoints.length - 1] as [number, number];
      const marker = createPathHistoryMarker(initialPoint);
      map.markers.add(marker);
      markerRef.current = marker;
      initMarkerPopup();
    }

    if (markerRef.current && partialPoints.length > 0) {
      const lastPoint = partialPoints[partialPoints.length - 1];
      markerRef.current.setOptions({ position: lastPoint });

      if (typeof onSliderDateChange === 'function') {
        onSliderDateChange(alignedData[maxIndex]?.date || toDateUpdated || '');
      }

      if (popupRef.current) {

        const dateStr = partialDate[partialDate.length - 1] || '';
        const [datePart, timePart] = dateStr.split(" ");
        const [day, month, year] = datePart.split("/");
        const [hours, minutes, seconds] = timePart.split(":");

        const utcDate = new Date(Date.UTC(
          year,
          month - 1,
          day,
          hours,
          minutes,
          seconds
        ));

        const localDate = new Date(utcDate);

        popupRef.current.setOptions({
          position: lastPoint,
          content: `<div class='popupMarkerLineChart'>
                      <span class='popupMarkerLineChart-vertical-item'>
                        <span class='popupMarkerLineChart-horizontal-item'>${localizationService.toLanguageString('azure.signals', 'NOT_FOUND')}:</span>
                        <span>${countWakeups}</span>
                      </span>
                      <span class='popupMarkerLineChart-vertical-item'>                        
                            <span>${selectedDevices}</span>
                        </span>                    
                        <span class='popupMarkerLineChart-vertical-item'>                        
                            <span class='popupMarkerLineChart-horizontal-item'>
                                ${localizationService.toLanguageString('configuration.analysis.path_history.accuracy', 'NOT_FOUND')}:
                            </span>
                            <span>${infoDevice[0][5].toFixed(2)}</span>
                        </span>
                        <span class='popupMarkerLineChart-vertical-item'>
                            <span class='popupMarkerLineChart-horizontal-item'>
                                D:
                            </span> 
                            <span>
                            ${localDate.toLocaleString()}
                            </span>
                        </span>
                        
                        <span class='popupMarkerLineChart-vertical-item'>
                            <span class='popupMarkerLineChart-horizontal-item'>
                                LD:
                            </span>
                            <span>${infoDevice[0][4]}</span>
                        </span>
                    </div>`,
        });
      }
    }
  };

  const initMarkerPopup = () => {
    if (!markerRef.current || !popupRef.current) return;

    const tryAttachHover = () => {
      const el = markerRef.current?.getElement?.();
      if (!el) {
        setTimeout(tryAttachHover, 100);
        return;
      }

      if (el.dataset.hoverAttached) return;
      el.dataset.hoverAttached = 'true';

      el.onmouseenter = () => {
        if (popupRef.current && mapRef?.current) {
          popupRef.current.open(mapRef?.current);
        }
      };

      el.onmouseleave = () => {
        popupRef.current?.close();
      };
    };

    tryAttachHover();
  };

  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = parseInt(e.target.value, 10);
    setPathStep(value);

    // update date
    if (alignedData.length > 0 && value >= 0) {
      const fromIdx = 0;
      const toIdx = Math.min(value, alignedData.length - 1);

      const from = alignedData[fromIdx]?.date || sliderFromDate;
      const to = alignedData[toIdx]?.date || sliderToDate;

      setFromDateUpdated(from);
      setToDateUpdated(to);
      if (typeof onSliderDateChange === 'function') {
        onSliderDateChange(to || '');
      }
    }

    updateLines(value);
  };

  const maxSlider = alignedData.length;

  const createPathHistoryMarker = (position: [number, number]) => {
    return new atlas.HtmlMarker({
      position,
      //anchor: 'top',
      pixelOffset: [0, 0],
      htmlContent: `
        <span style="display:inline-block; width:22px; height:30px;">
          <svg viewBox="0 0 24 24" width="22" height="30" fill="#198692" stroke="white" stroke-width="1.2" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 4.5 6 11 7 11s7-6.5 7-11c0-3.87-3.13-7-7-7zm0 9.5c-1.38 0-2.5-1.12-2.5-2.5S10.62 6.5 12 6.5s2.5 1.12 2.5 2.5S13.38 11.5 12 11.5z"/>
          </svg>
        </span>
      `
    });
  };

  // heatmap options panel
  useEffect(() => {
    if (!mapReady || !isHeatmapReady) return;

    const map: any = mapRef.current;
    if (!map || !mapContainer.current || map.sources === undefined) return;

    const sourceId = 'heatmap-source';
    const layerId = 'heatMap-layer';
    const existingSource = map.sources.getById(sourceId);
    const dataSource: any = existingSource || new atlas.source.DataSource(sourceId);

    if (!existingSource) {
      map.sources.add(dataSource);
    }

    heatmapDataSourceRef.current = dataSource;

    const existingLayer = map.layers.getLayerById(layerId);
    if (existingLayer) {
      map.layers.remove(existingLayer);
    }

    const heatmap = new atlas.layer.HeatMapLayer(dataSource, layerId, {
      radius,
      opacity,
      intensity,
      weight: ["get", "weight"],
      color: getAzureHeatmapColorExpression(showColor)
    });

    heatmapLayerRef.current = heatmap;
    map.layers.add(heatmap);

    // Initial draw.
    updateHeatmap(heatmapStep);
  }, [mapReady, isHeatmapReady, showColor]);

  const updateHeatmap = (s: number) => {
    if (!heatmapDataSourceRef.current) return;

    const dataSource = heatmapDataSourceRef.current;
    dataSource.clear();

    const clampedStep = Math.min(Math.max(s, 0), heatmapMaxSlider);


    coordinatesList.forEach((line: any) => {
      // Prendi i punti fino allo step selezionato (inclusivo)
      const partialPoints = getHeatmapPointsWithLikelihood(line).slice(0, clampedStep + 1);

      const lineWeight = typeof line.weight === 'number' ? line.weight : 1;

      partialPoints.forEach((point: { coord: [number, number], likelihood: number }) => {
        const pointWeight = showColor
          ? Math.max(0.05, point.likelihood)
          : lineWeight;

        dataSource.add(
          new atlas.data.Feature(
            new atlas.data.Point(point.coord),
            {
              // puoi passare proprietà che possono pesare nella heatmap
              //intensity: 1
              weight: pointWeight
            }
          )
        );
      });
    });
  };

  const handleSliderChangeHeatMap = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = Number(e.target.value);
    if (!Number.isFinite(value)) {
      return;
    }

    setHasTouchedHeatmapSlider(true);
    const clampedValue = Math.min(Math.max(value, 0), heatmapMaxSlider);
    setHeatmapStep(clampedValue);
    updateHeatmap(clampedValue);
  };

  useEffect(() => {
    if (heatmapLayerRef.current) {
      heatmapLayerRef.current.setOptions({
        radius,
        opacity,
        intensity
      });
    }
  }, [radius, opacity, intensity]);

  const userInteractingRef = useRef(false);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    const onMouseDown = () => userInteractingRef.current = true;
    const onMoveEnd = () => setTimeout(() => userInteractingRef.current = false, 200);

    map.events.add('mousedown', onMouseDown);
    map.events.add('moveend', onMoveEnd);

    return () => {
      map.events.remove('mousedown', onMouseDown);
      map.events.remove('moveend', onMoveEnd);
    };
  }, []);
  ``

  return (
    <div style={{ position: 'relative', height: '100%', width: '100%' }}>
      <button
        className='azure-reset-zoom'
        title={localizationService.toLanguageString('azure.reset_zoom', 'NOT_FOUND')}
        onClick={resetCenter}
      >
        <SvgIcon icon={currentLocation} className="icon" />
      </button>
      {/*{cleanedCoordinates?.length > 0 && showSlider && !hasExternalSliderControl && !isHeatmapReady &&
        <div
          style={{
            position: "absolute",
            width: '150px',
            height: '100px',
            top: '10px',
            left: "50px",
            zIndex: 1000,
            background: 'white'
          }}
        >
          <span style={{
            display: "block",
            color: '#198692',
            textAlign: 'center'
          }}>
            {localizationService.toLanguageString('azure.interval', 'NOT_FOUND')}
          </span>
          <input
            type="range"
            min="0"
            max={Math.max((maxSlider ?? 1) - 1, 0)}
            value={pathStep}
            onChange={handleSliderChange}
            style={{
              display: "block",
              margin: '0 auto',
              width: '80%',
              accentColor: '#198692'
            }}
          />
          <span style={{
            display: "block",
            color: '#198692',
            textAlign: 'center',
            fontSize: '12px'
          }}>
            {localizationService.toLanguageString('azure.from', 'NOT_FOUND')} - {localizationService.toLanguageString('azure.to', 'NOT_FOUND')}
          </span>
          <span style={{
            display: "block",
            color: '#198692',
            textAlign: 'center',
            fontSize: '12px'
          }}>
            {displayedFromDate && displayedToDate ? `${displayedFromDate} - ${displayedToDate}` : displayedFromDate || displayedToDate}
          </span>
        </div>
      }*/}
      {coordinatesList?.length > 0 && isHeatmapReady &&
        <ExpansionPanel
          title={localizationService.toLanguageString('azure.options', 'NOT_FOUND')}
          //subtitle={}
          expanded={expanded}
          tabIndex={0}
          //key={}
          onAction={() => setExpanded(!expanded)}
          style={{
            position: "absolute",
            width: '300px',
            top: '10px',
            left: "50px",
            zIndex: 1000,
            background: 'white'
          }}
        >
          <Reveal>
            {expanded && (
              <ExpansionPanelContent>

                {/*radius*/}
                <span className='heatmap-option'>
                  {localizationService.toLanguageString('azure.radius', 'NOT_FOUND')}: {radius}
                </span>
                <input
                  type="range"
                  min="5"
                  max="100"
                  value={radius}
                  onChange={(e) => setRadius(Number(e.target.value))}
                  className='heatmap-azure-slider'
                />

                {/*intensity*/}
                <span className='heatmap-option'>
                  {localizationService.toLanguageString('azure.intensity', 'NOT_FOUND')}: {intensity.toFixed(2)}
                </span>
                <input
                  type="range"
                  min="0.1"
                  max="5"
                  step="0.1"
                  value={intensity}
                  onChange={(e) => setIntensity(Number(e.target.value))}
                  className='heatmap-azure-slider'
                />

                {/*Opacity*/}
                <span className='heatmap-option'>
                  {localizationService.toLanguageString('azure.opacity', 'NOT_FOUND')}: {opacity.toFixed(2)}
                </span>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.05"
                  value={opacity}
                  onChange={(e) => setOpacity(Number(e.target.value))}
                  className='heatmap-azure-slider'
                />

                {/*interval*/}
                <span className='heatmap-option'>
                  {localizationService.toLanguageString('azure.interval', 'NOT_FOUND')}
                </span>
                <input
                  type="range"
                  min="0"
                  max={heatmapMaxSlider}
                  value={heatmapStep}
                  onChange={handleSliderChangeHeatMap}
                  className='heatmap-azure-slider'
                />
                {/*from - to*/}
                <span className='heatmap-option' style={{ fontSize: '12px' }}>
                  {localizationService.toLanguageString('azure.from', 'NOT_FOUND')} - {localizationService.toLanguageString('azure.to', 'NOT_FOUND')}
                </span>
                <span className='heatmap-option' style={{ fontSize: '12px' }}>
                  {displayedFromDate && displayedToDateHeatmap ? `${displayedFromDate} - ${displayedToDateHeatmap}` : displayedFromDate || displayedToDateHeatmap}
                </span>
              </ExpansionPanelContent>
            )}
          </Reveal>
        </ExpansionPanel>
      }
      <div ref={mapContainer} style={{ height: '100%', width: '100%' }} />
    </div>
  );

};